import { Money } from './Money'
import { Picture, type PictureDTO } from './Picture'
import { Promotion } from './Promotion'
import { Tag } from './Tag'
import type { Location } from './Location'

import config from '../server-config'

import { Service, ServiceCollection, ServiceMiniList, type ServiceDTO } from './Service'
import { capitalizeAllWords, normalizeForSearch } from '../util/string'
import { translateByKeyForLang, translateByKeySilentForLang } from '../util/translate'
import { supportedLangs } from '../lang-config'

export type SpaDTO = {
  uuid: string;
  uuid_revision: string;
  political_location_uuid: string;
  brand_id: string;
  name: string;
  status: 'online' | 'disabled' | 'closed' | 'closedtemp' | 'lost';
  status_quo: 'open' | 'premium' | 'grow' | 'start' | 'none' | 'closed';
  slug: string;
  uri: string;
  images: Array<PictureDTO>;
  translations: SpaTextsDTO[];
  city: string;
  address: string;
  postal_code: string;
  country: string;
  latitude: number;
  longitude: number;
  email: string;
  web: string;
  phone: string;
  services: ServiceDTO[];
  hotel_name: string;
  hotel_uuid: string;
  schedule: Array<{
    days: string;
    hours: string;
  }>;
  is_gift_voucher_available: boolean;
  rating: {
    value: number | null;
  } | null;
  hotel: {
    uuid: string;
    name: string;
    category: {
      type: string;
      name: string;
      value: number | null;
    };
  } | null;
  open_close_dates?: {
    close: number | undefined | null;
    open: number | undefined | null;
  };
  sales_configuration?: {
    days_before_voucher_expirate: number,
    spa_book_conditions: Array<string>
  }
};

export interface SpaTextsDTO {
  langcode: string;
  slug: string;
  uri: string;
  long_description: string;
  short_description: string;
  legal_conditions: {
    legal_notice: string;
    political_condition: string;
    general_condition: string;
  };
}

export class SpaTexts {
  constructor(
    public slug: string,
    public uri: string,
    public longDescription: string,
    public shortDescription: string,
    public legalNotice: string,
    public politicalCondition: string,
    public generalCondition: string,
  ) {}

  static from(dto: SpaTextsDTO): SpaTexts {
    return new SpaTexts(
      dto.slug,
      dto.uri,
      dto.long_description,
      dto.short_description,
      dto.legal_conditions.legal_notice,
      dto.legal_conditions.political_condition,
      dto.legal_conditions.general_condition,
    )
  }
}

function concatenateHotelLiteralIfNeeded(originalName: string): string {
  if (!originalName) {
    return originalName
  }

  const lowercase = originalName.toLowerCase()

  if (lowercase.includes('hotel') || lowercase.includes('resort') || lowercase.includes('apartamento')) {
    return originalName
  } else {
    return `Hotel ${originalName}`
  }
}

function extractHotelData(dto: SpaDTO): Spa['hotel'] {
  if (!dto.hotel_name && !dto.hotel) {
    return {
      uuid: '',
      name: '',
      category: 0,
    }
  }

  let uuid = dto.hotel_uuid
  let name = dto.hotel_name
  let category = 0

  if (dto.hotel) {
    uuid = dto.hotel.uuid ?? uuid
    name = dto.hotel.name ?? name

    if (dto.hotel.category) {
      category = dto.hotel.category.value || 0
    }
  }

  name = concatenateHotelLiteralIfNeeded(name)
  name = capitalizeAllWords(name)

  return {
    uuid,
    name,
    category,
  }
}

export interface SpaLocation {
  address: string,
  state: string,
  country: string,
}

function translateScheduleLabel(label: string): string {
  const days = [
    'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo', 'festivos'
  ]

  let finalLabel = normalizeForSearch(label)
  for (const day of days) {
    const transDay = translateByKeySilentForLang(day, config.runtime.language)
    if (transDay) {
      finalLabel = finalLabel.replaceAll(day, transDay)
    } else {
      finalLabel = label
    }
  }

  return capitalizeAllWords(finalLabel)
}

export type SpaSaleConfig = {
  daysBeforeVoucherExpirate: number,
}

export class Spa {
  constructor(
    public uuid: string,
    public uuidRevision: string,
    public name: string,
    public slug: string,
    public uri: string,
    public status: SpaDTO['status'],
    public statusQUO: SpaDTO['status_quo'],
    public locationUUID: string,
    public brandId: string,
    public texts: Record<string, SpaTexts>,
    public location: SpaLocation,
    public images: Picture[],
    public services: Service[],
    public schedule: Record<string, Array<string>>,
    public giftVoucherAvailable: boolean,
    public score: number, // Rating
    public promoTags: Array<{ tag: Tag, price: Money }>,
    public hotel: {
      uuid: string,
      name: string,
      category: number,
    },
    public salesConfig: SpaSaleConfig,
    // TODO: Spa: public openingCloseTime?: { from: Date; to: Date; }
  ) {
  }

  static from(dto: SpaDTO, location: Location | null): Spa {
    // FIXME: Esto igual habría que refactorizarlo en una Location
    const spaLocation: SpaLocation = {
      address: dto.address,
      state: dto.city,
      country: location ? location.countryCode : 'ES',
    }

    const images = dto.images.map(Picture.from)
    if (!images.length) {
      const picture = Picture.getDefaultImage()
      images.push(picture)
    }

    const salesConfig = {
      daysBeforeVoucherExpirate: 0
    }

    if (dto.sales_configuration && dto.sales_configuration.days_before_voucher_expirate) {
      salesConfig.daysBeforeVoucherExpirate = dto.sales_configuration.days_before_voucher_expirate
    }

    const services = dto.services.map((servDto) => {
      const service = Service.from(servDto, spaLocation, salesConfig.daysBeforeVoucherExpirate)
      Service.addExtraImages(service, images, 1)
      return service
    })

    const schedule: Record<string, Array<string>> = {}
    dto.schedule.forEach((item) => {
      const daysLabel = translateScheduleLabel(item.days)

      if (!schedule[daysLabel]) {
        schedule[daysLabel] = []
      }

      schedule[daysLabel].push(item.hours)
    })

    let uri = dto.uri
    const texts: Record<string, SpaTexts> = {}

    dto.translations.forEach((trDto) => {
      if (!supportedLangs.includes(trDto.langcode)) {
        return
      }

      texts[trDto.langcode] = SpaTexts.from(trDto)

      if (trDto.langcode === config.runtime.language && trDto.uri) {
        uri = trDto.uri
      }
    })

    const spa = new Spa(
      dto.uuid,
      dto.uuid_revision,
      dto.name,
      dto.slug,
      uri,
      dto.status,
      dto.status_quo,
      dto.political_location_uuid,
      dto.brand_id,
      texts,
      spaLocation,
      images,
      services,
      schedule,
      dto.is_gift_voucher_available ?? false,
      dto.rating !== null ? Number(dto.rating.value) : 0,
      [],
      extractHotelData(dto),
      salesConfig
    )

    Spa.appendPromotionTagsIfIsEmpty(spa)

    return spa
  }

  static getStatusLabelTransKey(status: Spa['status']): string {
    switch (status) {
      case 'online':
        return 'spa-status-online'
      case 'disabled':
        return 'spa-status-disabled'
      case 'closed':
        return 'spa-status-closed'
      case 'closedtemp':
        return 'spa-status-closedtemp'
      case 'lost':
        return 'spa-status-lost'
    }

    return 'spa-status-lost'
  }

  static appendPromotionTagsIfIsEmpty(self: Spa) {
    if (self.promoTags.length) {
      return
    }

    // Add existing promo tags
    Promotion.publicPromotionUUIDs
      .forEach((tagUUID) => {
        const serviceCollection = new ServiceCollection(self.services)
        serviceCollection.filterByPromotionCategoryUUID(tagUUID)
        serviceCollection.sortByCheaper(new Date())

        if (serviceCollection.services.length) {
          const cheapestServiceByPromotionTag = serviceCollection.services[0]

          const tags = cheapestServiceByPromotionTag.promotionTags.filter((tag) => tag.uuid === tagUUID)
          if (tags.length) {
            const tag = tags[0]
            self.promoTags.push({ tag, price: cheapestServiceByPromotionTag.pricePerPerson })
          }
        }
      })
  }

  public static toJSONServiceLess(self: Spa): string {
    const copy: Spa = JSON.parse(JSON.stringify(self))
    copy.services = []
    return JSON.stringify(copy)
  }
}

export interface SpaMiniSearchTexts {
  langcode: string;
  uri: string;
}

export class SpaMiniSearch {
  constructor(
    public uuid: string,
    public name: string,
    public slug: string,
    public uri: string,
    public texts: Record<string, SpaMiniSearchTexts>,
    public location: SpaLocation,
    public hotelName: string,
  ) {
  }

  static from(spa: Spa, targetLang: string): SpaMiniSearch {
    let slug = spa.slug
    let uri  = spa.uri

    const texts: Record<string, SpaMiniSearchTexts> = {}
    Object.entries(spa.texts).forEach(([langcode, value]) => {
      texts[langcode] = {
        langcode,
        uri: value.uri,
      }

      if (targetLang === langcode && value.slug && value.uri) {
        slug = value.slug
        uri  = value.uri
      }
    })

    return new SpaMiniSearch(
      spa.uuid,
      spa.name,
      slug,
      uri,
      texts,
      spa.location,
      spa.hotel.name,
    )
  }
}

export interface SpaMiniListTexts {
  langcode: string;
  shortDescription: string;
}

export class SpaMiniList {
  constructor(
    public uuid: string,
    public name: string,
    public uri: string,
    public status: SpaDTO['status'],
    public statusQUO: SpaDTO['status_quo'],

    public texts: Record<string, SpaMiniListTexts>,
    public location: SpaLocation,

    public images: Array<Picture>,
    public services: Array<ServiceMiniList>,

    public giftVoucherAvailable: boolean,

    public score: number, // Rating
    public hotel: {
      uuid: string,
      name: string,
      category: number,
    },
    public promoTags: Array<{ tag: Tag, price: Money }>,
  ) {}

  static from(spa: Spa): SpaMiniList {
    const services = spa.services.map(ServiceMiniList.from)
    const texts: Record<string, SpaMiniListTexts> = {}

    Object.entries(spa.texts).forEach(([langcode, text]) => {
      texts[langcode] = {
        langcode,
        shortDescription: text.shortDescription,
      }
    })

    const images = spa.images.slice(0, 1)

    return new SpaMiniList(
      spa.uuid,
      spa.name,
      spa.uri,
      spa.status,
      spa.statusQUO,

      texts,
      spa.location,

      images,
      services,

      spa.giftVoucherAvailable,

      spa.score,
      spa.hotel,
      spa.promoTags,
    )
  }
}
